import { Component, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, BehaviorSubject } from "rxjs";
import { switchMap } from "rxjs/operators";
import { AppService } from "src/app/app.component";
import { LANGUAGES } from "src/app/constant";
import { User } from "src/app/models/entity.model";
import { AttributeScope } from "src/app/models/telemetry.model";
import { AuthService } from "src/app/services/auth.service";
import { FarmerService } from "src/app/services/device.service";
import { AttributeService } from "src/app/services/entity.service";
import { PaymentService } from "src/app/services/payment.service";
import { TEXT } from "src/app/texts";
import { environment } from "src/environments/environment";
import { CropCallibrationComponent } from "./callibration";
import { AgModal } from "src/app/util";
import { TelemetryService, TelemetrySubscription } from "src/app/services/telemetry";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/state";


const ENABLE_EMAIL_ATTRIBUTE = 'enable_email'
const LOCALE_ATTRIBUTE = 'locale'
const IS_AMBIENT_HUMIDITY = 'is_ambient_humidity'

export interface IProfileSettings {
  is_ambient_humidity: boolean
  locale: string | null
  enable_email: boolean
}

@Injectable()
export class ProfileService {
  settings: BehaviorSubject<IProfileSettings> = new BehaviorSubject(
    {enable_email: false, locale: null, is_ambient_humidity: false}
  )

  constructor (
    private auth: AuthService,
    private attributes: AttributeService,
    private telemetry: TelemetryService,
    private store: Store<AppState>,
    private appService: AppService
  ) {
    this.load()
  }

  // the user attributes are followed over the websocket (no http request, and changes arrive)
  private subscription: TelemetrySubscription = null
  private defaultsChecked = false

  load () {
    this.store.select(state => state.auth?.userDetails?.id).subscribe(userId => {
      this.subscription?.unsubscribe()
      this.subscription = null
      this.defaultsChecked = false
      if (!userId) return
      this.subscription = this.telemetry.latest(userId, {
        attributes: [ENABLE_EMAIL_ATTRIBUTE, LOCALE_ATTRIBUTE, IS_AMBIENT_HUMIDITY],
        label: 'user settings', source: 'profile'
      }, {
        onData: (resp) => {
          let newSettings = {...this.settings.value}
          let value = (key: string) => resp.data[key] && resp.data[key].length ? resp.data[key][0][1] : undefined
          // attribute values can arrive as booleans or as strings
          let isTrue = (v: any) => v === true || v === 'true'
          if (value(ENABLE_EMAIL_ATTRIBUTE) !== undefined) newSettings.enable_email = isTrue(value(ENABLE_EMAIL_ATTRIBUTE))
          if (value(LOCALE_ATTRIBUTE) !== undefined) newSettings.locale = value(LOCALE_ATTRIBUTE) || null
          if (value(IS_AMBIENT_HUMIDITY) !== undefined) newSettings.is_ambient_humidity = isTrue(value(IS_AMBIENT_HUMIDITY))
          this.settings.next(newSettings)
          if (resp.attributesLoaded && !this.defaultsChecked) {
            this.defaultsChecked = true
            this.saveDefaults(userId, {[ENABLE_EMAIL_ATTRIBUTE]: value(ENABLE_EMAIL_ATTRIBUTE)})
          }
          return true
        },
        onLoading: () => {}
      })
    })
  }

  // attributes a user must have, created the first time they are missing.
  // NOTE: the locale is deliberately not one of them, it is only stored when the user picks
  //       a language on the profile page (see saveLocale)
  private async saveDefaults (userId, current: {[key: string]: any}) {
    let defaults = {[ENABLE_EMAIL_ATTRIBUTE]: true}
    let updates = Object.keys(defaults)
      .filter(key => current[key] === undefined)
      .map(key => ({key: key, value: defaults[key]}))
    if (updates.length == 0) return
    await this.attributes.saveEntityAttributes(userId, AttributeScope.SERVER_SCOPE, updates).toPromise()
  }

  // the language picked by the user on the profile page
  async saveLocale (locale: string) {
    await this.auth.getAuth().pipe(
      switchMap(auth => this.attributes.saveEntityAttributes(
        auth.userDetails.id, AttributeScope.SERVER_SCOPE, [{key: LOCALE_ATTRIBUTE, value: locale}]
      ))
    ).toPromise()
    this.settings.next({...this.settings.value, locale: locale})
  }

  // everything but the locale, which has its own rules (see saveLocale)
  async saveSettings (settings: IProfileSettings) {
    await this.auth.getAuth().pipe(
      switchMap(auth => {
        return this.attributes.saveEntityAttributes(
          auth.userDetails.id, AttributeScope.SERVER_SCOPE, [
            {key: ENABLE_EMAIL_ATTRIBUTE, value: settings.enable_email},
            {key: IS_AMBIENT_HUMIDITY, value: settings.is_ambient_humidity}
          ]
        )
      })
    ).toPromise()
    this.settings.next({...this.settings.value, enable_email: settings.enable_email, is_ambient_humidity: settings.is_ambient_humidity})
  }
}

@Component({
  template: `
  <ion-header>
  <ion-toolbar>
  <ion-title mode="md" [translate]="text.profile.my_profile"></ion-title>
  <ion-buttons slot="primary">
      <ion-button (click)="dismiss()" >
          <ion-icon name="close-outline"></ion-icon>
      </ion-button>
      </ion-buttons>
  </ion-toolbar>
  </ion-header>
  <ion-content class="dialog">
    <div *ngIf="loading" [translate]="text.general.loading"></div>
    <div *ngIf="!loading" class="dialog-content">

      <ion-list>
        <ion-item *ngIf="userState">
          <ion-label>
            <div>{{userState.firstName}} {{userState.lastName}}</div>
            <div>{{userState.email}}</div>
          </ion-label>
        </ion-item>
        <!--<ion-item lines="none" *ngIf="userState">
          <ion-label>
            {{userState.email}}
          </ion-label>  
        </ion-item>-->
        <ion-item >
          <ion-checkbox [translate]="text.profile.email_notifications" (ionChange)="onToggleEmail($event)" [(ngModel)]="settings.enable_email"></ion-checkbox>
        </ion-item>
        <!--
        <ion-item >
          <ion-label *ngIf="reportConfig != null" [translate]="text.profile.email_weekly_report"></ion-label>
          <ion-checkbox *ngIf="reportConfig != null" (ionChange)="onWeeklyReportChange($event)" [(ngModel)]="reportConfig.subscribed"></ion-checkbox>
        </ion-item>
        -->
        <ion-item>
          <ion-select [(ngModel)]="settings.locale" (ngModelChange)="updateLocale()" interface="popover" >
            <ion-select-option value="" [translate]="text.general.select_language"></ion-select-option>
            <ion-select-option *ngFor="let lang of languages | sortLocale:'name_en'" 
              [value]="lang.locale">
              <span>{{lang.name_en}}</span>
              <span *ngIf="settings.locale != 'en' && lang.name_locale"> ({{lang.name_locale}})</span> 
            </ion-select-option>
          </ion-select>
          <!-- the language stored on the user differs from the one this device uses (e.g. an admin
               logged in as a customer), apply it on this device without changing the user -->
          <ion-button *ngIf="localeDiffers()" slot="end" size="small" fill="outline" (click)="applyLocale()"
            [translate]="text.general.apply"></ion-button>
        </ion-item>
        <ion-item button (click)="openCalibrations()">
          <ion-label>{{text.profile.moisture_calibration | translate}}</ion-label>
          <!--<ion-button (click)="openCalibrations()" color="medium">{{text.profile.moisture_calibration | translate}}</ion-button>-->
        </ion-item>
        <ion-item>
          <ion-checkbox [translate]="text.profile.show_ambient_as_humidity" (ionChange)="saveSettings()" [(ngModel)]="settings.is_ambient_humidity"></ion-checkbox>
        </ion-item>

        <ion-item>
          <ion-button color="primary" (click)="logout()" [translate]="text.profile.logout"></ion-button>
        </ion-item>

        <ion-accordion-group style="margin-top: 20px">
          <ion-accordion value="advanced">
            <ion-item slot="header" >
              <ion-label>{{text.profile.advanced | translate}}</ion-label>
            </ion-item>
            <div class="ion-padding" slot="content">
            <!--<ion-item button (click)="downloadReport()">
              <ion-label [translate]="text.profile.send_telemetry_data_title"></ion-label>
            </ion-item>-->
            <ion-item button (click)="showChangePassword()">
              <ion-label  [translate]="text.profile.change_password"></ion-label>
            </ion-item>
              <ion-item>
                <ion-button style="margin-left: auto;" color="danger" (click)="deleteAccount()" [translate]="text.profile.delete_account"></ion-button>
              </ion-item>
            </div>
          </ion-accordion>
        </ion-accordion-group>
        
      </ion-list>
      
    </div>
  </ion-content>
  `,
  styleUrls: ['login-page.scss']
})
export class ProfilePageComponent {

  constructor (
    private farmer: FarmerService, 
    private dialogRef: ModalController,
    private auth: AuthService,
    private alertController: AlertController, 
    private payment: PaymentService,
    private attributes: AttributeService,
    private translate: TranslateService,
    private appService: AppService,
    private router: Router,
    private modalController: ModalController,
    private agModal: AgModal,
    private profile: ProfileService
  ) {}

  text = TEXT
  loading = true
  
  userState: User
  languages = LANGUAGES

  isProduction = environment.key == 'live'
  settings: IProfileSettings

  // the language shown is the user's, the app may be using another one stored on this device
  localeDiffers () {
    return !!this.settings?.locale && this.settings.locale != this.appService.locale
  }
  // use the user's language on this device, the user itself is not changed
  applyLocale () {
    this.appService.setLocale(this.settings.locale)
  }

  // the only place where the locale is stored on the user
  async updateLocale () {
    this.appService.setLocale(this.settings.locale)
    await this.profile.saveLocale(this.settings.locale)
    await this.payment.updateCustomer(this.settings.locale)
  }

  _subscriptions: Subscription[] = []
  listen (s: Subscription) { this._subscriptions.push(s) }
  ngOnDestroy () { this._subscriptions.map(s => s.unsubscribe()); this._subscriptions = [] }

  ngOnInit () {
    this.loadReport()
    this.listen(
      this.profile.settings.subscribe(settings => {
        // without a stored language the one the app is using is shown
        this.settings = {...settings, locale: settings.locale || this.appService.locale}
        this.loading = false
      })
    )
    this.listen(
      this.auth.getAuth().subscribe(auth => {
        this.userState = auth.userDetails
      })
    )
  }

  reportConfig
  async loadReport () {
    let result = await this.payment._request('GET', 'report/config')
    this.reportConfig = result
  }
  async onWeeklyReportChange (evt) {
    let checked = evt.target.checked
    if (!this.reportConfig) throw new Error('no report config')
    this.reportConfig.subscribed = checked
    await this.saveReport()
  }
  async saveReport () {
    await this.payment._request('POST', 'report/config', this.reportConfig)
    await this.loadReport()
  }

  async downloadReport () {
    const alert = await this.alertController.create({
      cssClass: 'my-custom-class',
      header: this.translate.instant(this.text.profile.send_telemetry_data_title),
      message: this.translate.instant(this.text.profile.send_telemetry_data_info),
      inputs: [
        {
          name: 'email',
          value: this.userState.email,
        }
      ],
      buttons: [
        {
          text: this.translate.instant(this.text.general.cancel),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {}
        }, 
        {
          text: this.translate.instant(this.text.general.ok),
          handler: async (data) => {
            try {
              await this.payment._request('POST', 'report/build', {full: true, email: data.email})
              const errorAlert = await this.alertController.create({
                message: this.translate.instant(this.text.profile.send_telemetry_data_success)
              })
              await errorAlert.present()
            } catch (err) {
              const errorAlert = await this.alertController.create({
                message: this.translate.instant(this.text.profile.send_telemetry_data_failed)
              })
              await errorAlert.present()
            }
            
          }
        }
      ]
    })
    await alert.present()
  }

  async saveSettings () {
    await this.profile.saveSettings(this.settings)
  }

  async openCalibrations () {
    const dialogRef = await this.modalController.create({
      component: CropCallibrationComponent, componentProps: {}
    });
    const { data } = await this.agModal.openModal(dialogRef)
  }
  

  

  async onToggleEmail (evt) {
    let checked = evt.target.checked
    this.settings.enable_email = checked
    await this.saveSettings()
  }

  async showChangePassword () {
    const alert = await this.alertController.create({
      cssClass: 'my-custom-class',
      header: this.translate.instant(this.text.profile.change_password),
      inputs: [
        {
          name: 'currentPassword',
          type: 'password',
          placeholder: this.translate.instant(this.text.profile.current_password)
        },
        {
          name: 'newPassword',
          type: 'password',
          placeholder: this.translate.instant(this.text.profile.new_password)
        }
      ],
      buttons: [
        {
          text: this.translate.instant(this.text.general.cancel),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
          }
        }, 
        {
          text: this.translate.instant(this.text.general.ok),
          handler: async (data) => {
            if (!data.currentPassword || !data.newPassword) return false
            try {
              this.farmer.changePassword(data)
            } catch (err) {
              const errorAlert = await this.alertController.create({
                message: this.translate.instant(this.text.profile.change_password_failed)
              })
              errorAlert.present()
            }
          }
        }
      ]
    })
    await alert.present();
  }

  async deleteAccount () {
    const alert = await this.alertController.create({
      cssClass: 'my-custom-class',
      header: this.translate.instant(this.text.profile.delete_account_title),
      message: this.translate.instant(this.text.profile.delete_account_confirm),
      buttons: [
        {
          text: this.translate.instant(this.text.general.cancel),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
          }
        }, 
        {
          text: this.translate.instant(this.text.general.yes),
          handler: async (data) => {
            let message = this.translate.instant(this.text.profile.delete_account_success) // "Your request have been received. You will receive a confirmation mail when your request for deletion have been processed. Please contact sales@supertech.dk for any question."
            //let header = "Request received"
            try {
              await this.payment.sendDeletionRequest()
            } catch (err) {
              message = this.translate.instant(this.text.profile.delete_account_failed) // "Failed to send request. Please contact us directly on sales@supertech.dk"
            }
            let confirmAlert = await this.alertController.create({
              cssClass: 'my-custom-class',
              header: this.translate.instant(this.text.profile.delete_account_title),
              message: message,
              buttons: [
                {
                  text: this.translate.instant(this.text.general.ok),
                  role: 'cancel',
                  cssClass: 'secondary',
                  handler: () => {
                  }
                }
              ]
            })
            await confirmAlert.present();
            
            /*
            let confirmAlert = await this.alertController.create({
              cssClass: 'my-custom-class',
              header: this.translate.instant(this.text.profile.delete_account_title),
              message: this.translate.instant(this.text.profile.delete_confirm_input_message),
              inputs: [
                {
                  placeholder: 'delete',
                  attributes: {},
                  handler: (input) => {
                  }
                }
              ],
              buttons: [
                {
                  text: this.translate.instant(this.text.general.cancel),
                  handler: () => {
                  }
                },
                {
                  text: this.translate.instant(this.text.general.confirm),
                  role: 'cancel',
                  cssClass: 'secondary',
                  handler: async (data) => {
                    
                  }
                }
              ]
            })
            await confirmAlert.present();*/
            
          }
        }
      ]
    })
    await alert.present();
  }

  logout () {
    //console.error('LOGOUT')
    this.auth.logout()
    this.dismiss()
  }

  dismiss () {
    this.dialogRef.dismiss()
  }
}
